/**
 * Subject聚合态 Fixture 数据
 * 覆盖任务：T-R1/T-R4/T-R5/T-R6/T-R7/T-R8/T-R9/T-R10/T-R11/T-R12
 */

const nowIso = () => new Date().toISOString();

const cloneDeep = (value) => JSON.parse(JSON.stringify(value));

const toTrimmedString = (value) => {
    const normalized = String(value || '').trim();
    return normalized || null;
};

const UNIQUE_SEPARATOR = '::';

const uniqCapabilityCodes = (codes = []) => {
    const seen = new Set();
    const result = [];

    codes.forEach((code) => {
        const normalized = toTrimmedString(code);
        if (!normalized || seen.has(normalized)) {
            return;
        }
        seen.add(normalized);
        result.push(normalized);
    });

    return result;
};

const DEFAULT_GRANTED_CAPABILITY_CODES = Object.freeze([
    'result_guide.luminosity_histogram',
    'result_guide.rgb_histogram',
    'result_guide.luminance_rgb_mask',
    'result_guide.gaps_text_description',
    'summary.ai_gaps_summary',
    'summary.retouch_workflow',
    'compare.luminance_rgb_mask',
    'result_task.default',
    'result_task.gaps_card_overall',
    'atomic_capability.image_tab',
    'atomic_capability.tonal_exposure',
    'atomic_capability.color_grading',
    'atomic_capability.global_color_base'
]);

const DEFAULT_UNLOCKABLE_CAPABILITY_CREDITS = Object.freeze({
    'compare.point_inspector': 1,
    'atomic_capability.global_color_palette': 1,
    'result_guide.gaps_visual_panel': 1
});

const DEFAULT_UNAVAILABLE_CAPABILITY_REASONS = Object.freeze({
    'compare.finetune_slider': 'unavailable_for_tier',
    'result_guide.hue_mapping_visual': 'backend_not_ready',
    'result_guide.clipping_mapping_visual': 'backend_not_ready',
    'result_guide.zone_boxplot_snapshot_visual': 'backend_not_ready'
});

const createCapabilityEntry = ({
    capabilityCode,
    availability,
    credits = 0,
    confirmationRequired = false,
    lockedReason = null,
    reasonCode = null
}) => {
    const normalizedCapabilityCode = toTrimmedString(capabilityCode);
    const numericCredits = Number.isFinite(Number(credits)) ? Number(credits) : 0;

    return {
        capability_code: normalizedCapabilityCode,
        code: normalizedCapabilityCode,
        capability_delivery_kind: 'frontend_only',
        availability,
        visible_fields: '*',
        display_price: {
            credits: numericCredits
        },
        confirmation_required: Boolean(confirmationRequired),
        unlock_requirements: availability === 'locked'
            ? {
                credits: numericCredits,
                tier_requirement: null
            }
            : null,
        locked_reason: lockedReason,
        reason_code: reasonCode,
        granted_at: availability === 'available' ? nowIso() : null
    };
};

const normalizeCapabilityCodeFromEntry = (entry) => toTrimmedString(
    entry?.capability_code || entry?.capabilityCode || entry?.code
);

const dedupeCapabilityEntries = (entries = []) => {
    const dedupeMap = new Map();
    entries.forEach((entry) => {
        const code = normalizeCapabilityCodeFromEntry(entry);
        if (!code) {
            return;
        }
        dedupeMap.set(code, {
            ...entry,
            capability_code: code,
            code
        });
    });
    return Array.from(dedupeMap.values());
};

const buildCapabilityCollections = ({
    grantedCapabilityCodes = [],
    unlockedCapabilityCodes = [],
    unlockableCapabilityCredits = {},
    unavailableCapabilityReasons = {},
    inactiveGrantedCapabilityCodes = []
} = {}) => {
    const normalizedUnlockedCodes = uniqCapabilityCodes(unlockedCapabilityCodes);
    const inactiveGrantedCodes = uniqCapabilityCodes(inactiveGrantedCapabilityCodes);
    const grantedCodes = uniqCapabilityCodes([
        ...grantedCapabilityCodes,
        ...normalizedUnlockedCodes
    ]).filter((code) => !inactiveGrantedCodes.includes(code));

    const unlockableCodes = uniqCapabilityCodes(Object.keys(unlockableCapabilityCredits)).filter(
        (code) => !grantedCodes.includes(code) && !inactiveGrantedCodes.includes(code)
    );

    const unavailableCodes = uniqCapabilityCodes(Object.keys(unavailableCapabilityReasons)).filter(
        (code) => !grantedCodes.includes(code) && !unlockableCodes.includes(code) && !inactiveGrantedCodes.includes(code)
    );

    return {
        capabilities: dedupeCapabilityEntries(grantedCodes.map((capabilityCode) => createCapabilityEntry({
            capabilityCode,
            availability: 'available'
        }))),
        unlockable_capabilities: dedupeCapabilityEntries(unlockableCodes.map((capabilityCode) => createCapabilityEntry({
            capabilityCode,
            availability: 'locked',
            credits: unlockableCapabilityCredits[capabilityCode] || 0,
            confirmationRequired: true,
            lockedReason: 'requires_unlock'
        }))),
        unavailable_capabilities: dedupeCapabilityEntries(unavailableCodes.map((capabilityCode) => createCapabilityEntry({
            capabilityCode,
            availability: 'unavailable',
            reasonCode: unavailableCapabilityReasons[capabilityCode] || 'unavailable_for_tier',
            lockedReason: unavailableCapabilityReasons[capabilityCode] || 'unavailable_for_tier'
        }))),
        inactive_granted_capabilities: dedupeCapabilityEntries(inactiveGrantedCodes.map((capabilityCode) => createCapabilityEntry({
            capabilityCode,
            availability: 'unavailable',
            reasonCode: 'tier_unavailable_granted',
            lockedReason: 'tier_unavailable_granted'
        })))
    };
};

const buildDefaultToneColorContext = ({ referenceCode }) => ({
    schema_version: 'tone-context-schema.v0.1',
    comparison_log: {
        source_image: 'processed',
        reference_image: referenceCode,
        tone_delta: 12.5,
        color_adjustment_suggestions: ['reduce_warmth', 'increase_contrast']
    }
});

/**
 * 默认结构化 analysis payload。
 * 与真实后端 analysis_payload 对齐（photone-analysis-api.v2 schema），
 * 满足 normalizeAnalysisPayload 的 SUPPORTED_SCHEMA_VERSIONS。
 * 让 completed subject 的 current_visible_result 携带结构化 gaps / overview，
 * 使 selectCurrentVisibleResultAnalysisPayload 可被 UI 消费链路直接消费。
 */
const DEFAULT_COMPLETED_ANALYSIS_PAYLOAD = Object.freeze({
    schema_version: 'photone-analysis-api.v2',
    overview: {
        summary_md: '整体色调偏暖，曝光略过，建议降低色温、压高光并适度提升饱和度。',
        post_processing_suggestions_md: '色温 -300K，高光 -0.3EV，阴影 +15，饱和度 +8。'
    },
    key_gaps: [
        { gap_id: 'gap-tone', title: '曝光层次差距', severity_code: 'medium', severity_label: 'Medium' },
        { gap_id: 'gap-color', title: '综合色温差距', severity_code: 'medium', severity_label: 'Medium' }
    ],
    gaps: [
        {
            id: 'gap-1',
            title: '综合色温差距',
            brief: '当前色调偏暖，中性灰偏黄，肤色略失自然。',
            severity_code: 'medium',
            severity_label: 'Medium',
            description_md: '## 色温\n建议降低色温至约 5500K，使中性灰回归，并保留肤色自然。'
        },
        {
            id: 'gap-2',
            title: '曝光层次差距',
            brief: '高光略微过曝约 0.3EV，暗部细节保留良好。',
            severity_code: 'low',
            severity_label: 'Low',
            description_md: '## 曝光\n降低高光恢复细节，阴影可适度提亮增强层次。'
        },
        {
            id: 'gap-3',
            title: '饱和节奏差距',
            brief: '当前色彩能量略不足，主色不够突出。',
            severity_code: 'low',
            severity_label: 'Low',
            description_md: '## 饱和度\n适度提升饱和度以突出主体，避免局部色彩层次拥挤。'
        }
    ]
});

const buildCurrentVisibleResult = ({
    resultId,
    status = 'ready',
    analysisMarkdown = null,
    supersedesResultId = null,
    upgradeGeneration = 1,
    aiEffectiveSourceKind = 'processed',
    aiEffectiveSourceHashId = null,
    analysisPayload = DEFAULT_COMPLETED_ANALYSIS_PAYLOAD
} = {}) => ({
    result_id: resultId || `res_${Date.now()}`,
    status,
    analysis_markdown: analysisMarkdown,
    analysis_payload: analysisPayload,
    access_mode: 'unlocked',
    granted_at: nowIso(),
    supersedes_result_id: supersedesResultId,
    upgrade_generation: upgradeGeneration,
    ai_effective_source_kind: aiEffectiveSourceKind,
    ai_effective_source_hash_id: aiEffectiveSourceHashId,
    result_images: [
        { type: 'color_palette', url: '/mock/images/palette_demo.png' },
        { type: 'tone_curve', url: '/mock/images/curve_demo.png' },
        { type: 'comparison', url: '/mock/images/comparison_demo.png' }
    ],
    generated_at: nowIso()
});

/**
 * 标准 Subject：runs 成功后、AI 待派发。
 */
const standardSubjectReady = (overrides = {}) => {
    const subjectId = overrides.subjectId || `sub_${Date.now()}`;
    const executionId = overrides.executionId || `exec_${Date.now()}`;
    const requestId = overrides.requestId || `req_${Date.now()}`;
    const runCode = overrides.runCode || 'upload_default_analysis';
    const operationType = overrides.operationType || 'run';
    const referenceCode = overrides.referenceCode || 'reference_1';
    const originalHashId = overrides.originalHashId || 'hash_original_demo';
    const processedHashId = overrides.processedHashId || `hash_processed_${Date.now()}`;
    const aiEffectiveSourceKind = overrides.aiEffectiveSourceKind || 'processed';
    const aiEffectiveSourceHashId = overrides.aiEffectiveSourceHashId
        || (aiEffectiveSourceKind === 'processed' ? processedHashId : originalHashId);

    const capabilityCollections = buildCapabilityCollections({
        grantedCapabilityCodes: overrides.grantedCapabilityCodes || DEFAULT_GRANTED_CAPABILITY_CODES,
        unlockedCapabilityCodes: overrides.unlockedCapabilityCodes || [],
        unlockableCapabilityCredits: overrides.unlockableCapabilityCredits || DEFAULT_UNLOCKABLE_CAPABILITY_CREDITS,
        unavailableCapabilityReasons: overrides.unavailableCapabilityReasons || DEFAULT_UNAVAILABLE_CAPABILITY_REASONS,
        inactiveGrantedCapabilityCodes: overrides.inactiveGrantedCapabilityCodes || []
    });

    const aiDispatchState = overrides.aiDispatchState || 'awaiting_dispatch';
    const requestable = overrides.requestable !== false;
    const pendingAiExecution = Object.prototype.hasOwnProperty.call(overrides, 'pendingAiExecution')
        ? overrides.pendingAiExecution
        : {
            execution_id: executionId,
            operation_type: operationType,
            ai_dispatch_state: aiDispatchState,
            target_generation: overrides.targetGeneration ?? null,
            requestable,
            job_id: null,
            status: null,
            request_id: requestId,
            retryable: false,
            retry_with_same_request_id: false,
            retry_request_id: null,
            ai_effective_source_kind: aiEffectiveSourceKind,
            ai_effective_source_hash_id: aiEffectiveSourceHashId,
            dispatch_payload: requestable
                ? {
                    execution_id: executionId,
                    request_id: requestId,
                    tone_color_adjustment_context: overrides.toneColorAdjustmentContext
                        || buildDefaultToneColorContext({ referenceCode })
                }
                : null,
            next_action: null,
            meta: {
                run_code: runCode
            }
        };

    return {
        subject_id: subjectId,
        subject_context: {
            original_hash_id: originalHashId,
            processed_image_hash: processedHashId,
            reference_code: referenceCode,
            run_code: runCode,
            anchor_original_image_hash_id: originalHashId,
            created_at: nowIso(),
            run_history: [
                {
                    execution_id: executionId,
                    run_code: runCode,
                    created_at: nowIso()
                }
            ]
        },
        viewer_state: {
            is_owner: true,
            can_view: true,
            can_manage: true,
            activation_epoch: Number.isFinite(Number(overrides.activationEpoch))
                ? Number(overrides.activationEpoch)
                : 2,
            current_unlocked_generation: Number.isFinite(Number(overrides.currentUnlockedGeneration))
                ? Number(overrides.currentUnlockedGeneration)
                : 1
        },
        capabilities: capabilityCollections.capabilities,
        unlockable_capabilities: capabilityCollections.unlockable_capabilities,
        unavailable_capabilities: capabilityCollections.unavailable_capabilities,
        inactive_granted_capabilities: capabilityCollections.inactive_granted_capabilities,
        ai_state: {
            ai_result_exists: false,
            ai_dispatch_state: aiDispatchState,
            dispatch_state: aiDispatchState,
            last_dispatch_at: null
        },
        current_visible_result: null,
        pending_ai_execution: pendingAiExecution,
        latest_overall_result: null,
        upgrade_offer: Object.prototype.hasOwnProperty.call(overrides, 'upgradeOffer')
            ? overrides.upgradeOffer
            : null,
        result_update_notice: Object.prototype.hasOwnProperty.call(overrides, 'resultUpdateNotice')
            ? overrides.resultUpdateNotice
            : null,
        entitlement_context: {
            tier: overrides.tier || 'standard',
            policy_version: overrides.policyVersion || '2026-04-05.r10.tier-free.v1',
            policy_etag: overrides.policyEtag || 'W/"analysis-policy-9e7b4f"',
            effective_tier: overrides.tier || 'standard'
        },
        upsell_hints: {
            unlock_candidates: ['summary.ai_gaps_summary'],
            tier_upgrade_candidates: ['pro'],
            recommended_action: 'unlock'
        },
        is_legacy: false,
        legacy_fallback_info: null,
        ...overrides.raw
    };
};

/**
 * AI 已完成 Subject。
 */
const subjectAiCompleted = (overrides = {}) => {
    const base = standardSubjectReady({
        ...overrides,
        aiDispatchState: 'completed',
        pendingAiExecution: null,
        unlockedCapabilityCodes: [
            'summary.ai_gaps_summary',
            ...(overrides.unlockedCapabilityCodes || [])
        ]
    });

    const analysisMarkdown = overrides.analysisMarkdown || `## 色彩分析报告\n\n### 整体色调\n主色调偏向暖色系（色温约5800K），以橙黄为主，辅以少量青蓝冷色点缀，形成温和对比。\n\n### 曝光分析\n- **高光**：略微过曝约0.3EV，建议降低高光恢复细节\n- **中间调**：分布均匀，覆盖直方图中央40%区域，层次丰富\n- **阴影**：保留约95%细节，未出现死黑，可安全提亮\n\n### 色彩建议\n1. 色温降低至5500K，增加清爽感\n2. 阴影提亮+15，增强画面层次\n3. 饱和度适当提升+8，突出主体肤色\n`;

    const resultId = overrides.resultId || `res_${Date.now()}`;
    const aiEffectiveSourceKind = overrides.aiEffectiveSourceKind || 'processed';
    const aiEffectiveSourceHashId = overrides.aiEffectiveSourceHashId
        || base?.subject_context?.processed_image_hash
        || base?.subject_context?.original_hash_id
        || null;

    return {
        ...base,
        ai_state: {
            ai_result_exists: true,
            ai_dispatch_state: 'completed',
            dispatch_state: 'completed',
            last_dispatch_at: nowIso()
        },
        current_visible_result: buildCurrentVisibleResult({
            resultId,
            status: 'ready',
            analysisMarkdown,
            supersedesResultId: overrides.supersedesResultId || null,
            upgradeGeneration: Number.isFinite(Number(overrides.upgradeGeneration))
                ? Number(overrides.upgradeGeneration)
                : 1,
            aiEffectiveSourceKind,
            aiEffectiveSourceHashId
        }),
        latest_overall_result: {
            result_id: resultId,
            status: 'ready',
            upgrade_generation: Number.isFinite(Number(overrides.upgradeGeneration))
                ? Number(overrides.upgradeGeneration)
                : 1,
            generated_at: nowIso(),
            ai_effective_source_kind: aiEffectiveSourceKind,
            is_visible_to_current_user: true
        },
        pending_ai_execution: null
    };
};

/**
 * AI 失败且可重试 Subject。
 */
const subjectAiFailedRetryable = (overrides = {}) => {
    const retryRequestId = overrides.retryRequestId || `retry_${Date.now()}`;
    const base = standardSubjectReady({
        ...overrides,
        aiDispatchState: 'failed'
    });

    return {
        ...base,
        ai_state: {
            ai_result_exists: Boolean(base.current_visible_result),
            ai_dispatch_state: 'failed',
            dispatch_state: 'failed',
            last_dispatch_at: nowIso()
        },
        pending_ai_execution: {
            execution_id: overrides.executionId || base?.pending_ai_execution?.execution_id || `exec_${Date.now()}`,
            operation_type: overrides.operationType || base?.pending_ai_execution?.operation_type || 'run',
            ai_dispatch_state: 'failed',
            target_generation: overrides.targetGeneration ?? null,
            requestable: true,
            job_id: overrides.jobId || null,
            status: 'failed',
            request_id: overrides.requestId || base?.pending_ai_execution?.request_id || `req_${Date.now()}`,
            retryable: true,
            retry_with_same_request_id: true,
            retry_request_id: retryRequestId,
            ai_effective_source_kind: overrides.aiEffectiveSourceKind || base?.pending_ai_execution?.ai_effective_source_kind || 'processed',
            ai_effective_source_hash_id: overrides.aiEffectiveSourceHashId || base?.pending_ai_execution?.ai_effective_source_hash_id || null,
            dispatch_payload: null,
            next_action: {
                type: 'retry_create_ai_job',
                method: 'POST',
                endpoint: '/analysis/ai-jobs/',
                execution_id: overrides.executionId || base?.pending_ai_execution?.execution_id || `exec_${Date.now()}`,
                retry_request_id: retryRequestId,
                params: {
                    execution_id: overrides.executionId || base?.pending_ai_execution?.execution_id || `exec_${Date.now()}`,
                    retry_request_id: retryRequestId
                },
                reason: 'AI 服务暂时不可用，建议稍后重试'
            }
        }
    };
};

/**
 * 已有可见结果且存在 paid upgrade 机会。
 */
const subjectWithUpgradeOffer = (overrides = {}) => {
    const targetGeneration = Number.isFinite(Number(overrides.targetGeneration))
        ? Number(overrides.targetGeneration)
        : 2;
    const currentGeneration = Number.isFinite(Number(overrides.currentUnlockedGeneration))
        ? Number(overrides.currentUnlockedGeneration)
        : Math.max(targetGeneration - 1, 1);
    const targetResultExists = overrides.targetResultExists === true;

    const base = subjectAiCompleted({
        ...overrides,
        upgradeGeneration: currentGeneration,
        currentUnlockedGeneration: currentGeneration
    });

    return {
        ...base,
        viewer_state: {
            ...base.viewer_state,
            current_unlocked_generation: currentGeneration
        },
        upgrade_offer: {
            run_code: overrides.runCode || base?.subject_context?.run_code || 'upload_default_analysis',
            upgrade_class: 'paid',
            current_unlocked_generation: currentGeneration,
            target_generation: targetGeneration,
            target_result_exists: targetResultExists,
            target_result_id: targetResultExists ? (overrides.targetResultId || `res_upgrade_${Date.now()}`) : null,
            display_price: {
                credits: Number.isFinite(Number(overrides.upgradeCredits))
                    ? Number(overrides.upgradeCredits)
                    : 6
            }
        },
        result_update_notice: {
            update_kind: targetResultExists ? 'latest_available' : null,
            effective_at: targetResultExists ? nowIso() : null,
            previous_result_id: base?.current_visible_result?.result_id || null,
            current_result_id: targetResultExists
                ? (overrides.targetResultId || `res_upgrade_${Date.now()}`)
                : (base?.current_visible_result?.result_id || null)
        }
    };
};

/**
 * Legacy 占位数据。
 */
const legacySubject = (overrides = {}) => ({
    subject_id: null,
    is_legacy: true,
    legacy_fallback_info: {
        original_hash_id: overrides.originalHashId || 'hash_legacy_old',
        preset_style_id: overrides.presetStyleId || 'portrait_studio_legacy'
    },
    capabilities: [],
    unlockable_capabilities: [],
    unavailable_capabilities: [],
    inactive_granted_capabilities: [],
    current_visible_result: null,
    pending_ai_execution: null,
    upgrade_offer: null
});

const cloneSubjectPayload = (subject = null) => {
    if (!subject || typeof subject !== 'object') {
        return null;
    }
    return cloneDeep(subject);
};

const applyCapabilityUnlockToSubject = (subject = null, capabilityCodes = []) => {
    if (!subject || typeof subject !== 'object') {
        return subject;
    }

    const unlockedCodes = uniqCapabilityCodes(capabilityCodes);
    if (unlockedCodes.length === 0) {
        return cloneSubjectPayload(subject);
    }

    const nextSubject = cloneSubjectPayload(subject);
    const currentCapabilityEntries = Array.isArray(nextSubject.capabilities)
        ? nextSubject.capabilities
        : [];
    const currentUnlockableEntries = Array.isArray(nextSubject.unlockable_capabilities)
        ? nextSubject.unlockable_capabilities
        : [];

    const unlockedKeySet = new Set(unlockedCodes);
    const newlyGrantedEntries = unlockedCodes.map((capabilityCode) => createCapabilityEntry({
        capabilityCode,
        availability: 'available'
    }));

    nextSubject.capabilities = dedupeCapabilityEntries([
        ...currentCapabilityEntries,
        ...newlyGrantedEntries
    ]);

    nextSubject.unlockable_capabilities = dedupeCapabilityEntries(
        currentUnlockableEntries.filter((entry) => !unlockedKeySet.has(normalizeCapabilityCodeFromEntry(entry)))
    );

    nextSubject.unavailable_capabilities = dedupeCapabilityEntries(
        (Array.isArray(nextSubject.unavailable_capabilities) ? nextSubject.unavailable_capabilities : []).filter(
            (entry) => !unlockedKeySet.has(normalizeCapabilityCodeFromEntry(entry))
        )
    );

    nextSubject.inactive_granted_capabilities = dedupeCapabilityEntries(
        (Array.isArray(nextSubject.inactive_granted_capabilities) ? nextSubject.inactive_granted_capabilities : []).filter(
            (entry) => !unlockedKeySet.has(normalizeCapabilityCodeFromEntry(entry))
        )
    );

    nextSubject.upsell_hints = {
        ...(nextSubject.upsell_hints || {}),
        unlock_candidates: (nextSubject.unlockable_capabilities || []).map((entry) => normalizeCapabilityCodeFromEntry(entry)).filter(Boolean),
        recommended_action: (nextSubject.unlockable_capabilities || []).length > 0 ? 'unlock' : 'none'
    };

    return nextSubject;
};

const stringifyCapabilitySetKey = (subjectId, capabilityCodes = []) => {
    const normalizedSubjectId = toTrimmedString(subjectId) || 'unknown_subject';
    const normalizedCodes = uniqCapabilityCodes(capabilityCodes).sort();
    return `${normalizedSubjectId}${UNIQUE_SEPARATOR}${normalizedCodes.join(',')}`;
};

/**
 * Result 页重入态 fixture：requestable=true + job_id 为空 + tone 未恢复
 * 用于验证前端在 tone 未恢复时不得调用 POST /analysis/ai-jobs/
 */
const subjectReentryAwaitingTone = (overrides = {}) => {
    const base = standardSubjectReady({
        ...overrides,
        aiDispatchState: 'awaiting_dispatch',
        requestable: true
    });

    const executionId = overrides.executionId || `exec_${Date.now()}`;
    const requestId = overrides.requestId || `req_${Date.now()}`;

    return {
        ...base,
        ai_state: {
            ai_result_exists: false,
            ai_dispatch_state: 'awaiting_dispatch',
            dispatch_state: 'awaiting_dispatch',
            last_dispatch_at: null
        },
        pending_ai_execution: {
            execution_id: executionId,
            operation_type: overrides.operationType || 'run',
            ai_dispatch_state: 'awaiting_dispatch',
            target_generation: overrides.targetGeneration ?? null,
            requestable: true,
            job_id: null,
            status: null,
            request_id: requestId,
            retryable: false,
            retry_with_same_request_id: false,
            retry_request_id: null,
            ai_effective_source_kind: overrides.aiEffectiveSourceKind || 'processed',
            ai_effective_source_hash_id: overrides.aiEffectiveSourceHashId || null,
            dispatch_payload: {
                execution_id: executionId,
                request_id: requestId,
                tone_color_adjustment_context: null
            },
            next_action: null
        }
    };
};

/**
 * AI failed roll-forward pending execution subject.
 * failure_code=provider_job_failed, recovery_mode=roll_forward_ai_result
 */
const failedRollForwardPendingSubject = (overrides = {}) => {
    const retryRequestId = overrides.retryRequestId || 'dispatch.retry.same.request';
    const executionId = overrides.executionId || 'exec_failed_roll_forward';
    const subjectId = overrides.subjectId || `subj_${Date.now()}`;

    return {
        subject_id: subjectId,
        subject_context: {
            run_code: 'upload_default_analysis',
            original_hash_id: overrides.originalImageHashId || 'mock_original_hash',
            processed_image_hash: overrides.processedImageHashId || 'mock_processed_hash',
            reference_code: overrides.referenceCode || 'mock_ref',
            ...overrides.subjectContext
        },
        ai_state: {
            ai_result_exists: false,
            ai_dispatch_state: 'failed',
            dispatch_state: 'failed',
            last_dispatch_at: nowIso()
        },
        current_visible_result: null,
        latest_overall_result: null,
        pending_ai_execution: {
            execution_id: executionId,
            operation_type: overrides.operationType || 'run',
            ai_dispatch_state: 'failed',
            requestable: true,
            retryable: true,
            retry_request_id: retryRequestId,
            retry_with_same_request_id: true,
            failure_code: 'provider_job_failed',
            recovery_mode: 'roll_forward_ai_result',
            next_action: {
                type: 'retry_create_ai_job',
                method: 'POST',
                endpoint: '/analysis/ai-jobs/',
                requires_tone_color_adjustment_context: true
            },
            ai_effective_source_kind: 'original',
            ai_effective_source_hash_id: 'mock_original_hash',
            ...overrides.pendingAiExecution
        },
        capabilities: overrides.capabilities || [],
        unlockable_capabilities: overrides.unlockableCapabilities || [],
        unavailable_capabilities: overrides.unavailableCapabilities || [],
        inactive_granted_capabilities: [],
        entitlement_context: {
            tier: overrides.tier || 'standard',
            policy_version: overrides.policyVersion || '2026-04-05.r10.tier-free.v1',
            policy_etag: overrides.policyEtag || 'W/"analysis-policy-9e7b4f"',
            effective_tier: overrides.tier || 'standard'
        },
        is_legacy: false,
        legacy_fallback_info: null,
        ...overrides.raw
    };
};

/**
 * AI failed delivery init retryable subject.
 * failure_code=delivery_init_failed_retryable, recovery_mode=retry_same_ai_result
 */
const deliveryInitFailedRetryableSubject = (overrides = {}) => {
    const base = failedRollForwardPendingSubject(overrides);
    return {
        ...base,
        pending_ai_execution: {
            ...base.pending_ai_execution,
            failure_code: 'delivery_init_failed_retryable',
            recovery_mode: 'retry_same_ai_result',
            ...overrides.pendingAiExecution
        }
    };
};

/**
 * Manual recovery pending subject.
 * requestable=false, retryable=false, recovery_mode=manual_recovery
 */
const manualRecoveryPendingSubject = (overrides = {}) => {
    const executionId = overrides.executionId || 'exec_manual_recovery';
    const subjectId = overrides.subjectId || `subj_${Date.now()}`;

    return {
        subject_id: subjectId,
        subject_context: {
            run_code: 'upload_default_analysis',
            original_hash_id: overrides.originalImageHashId || 'mock_original_hash',
            processed_image_hash: overrides.processedImageHashId || 'mock_processed_hash',
            reference_code: overrides.referenceCode || 'mock_ref',
            ...overrides.subjectContext
        },
        ai_state: {
            ai_result_exists: false,
            ai_dispatch_state: 'failed',
            dispatch_state: 'failed',
            last_dispatch_at: nowIso()
        },
        current_visible_result: null,
        latest_overall_result: null,
        pending_ai_execution: {
            execution_id: executionId,
            operation_type: overrides.operationType || 'run',
            ai_dispatch_state: 'failed',
            requestable: false,
            retryable: false,
            retry_request_id: null,
            retry_with_same_request_id: false,
            failure_code: 'manual_recovery_required',
            recovery_mode: 'manual_recovery',
            next_action: {
                type: 'manual_recovery'
            },
            ai_effective_source_kind: 'original',
            ai_effective_source_hash_id: 'mock_original_hash',
            ...overrides.pendingAiExecution
        },
        capabilities: overrides.capabilities || [],
        unlockable_capabilities: overrides.unlockableCapabilities || [],
        unavailable_capabilities: overrides.unavailableCapabilities || [],
        inactive_granted_capabilities: [],
        entitlement_context: {
            tier: overrides.tier || 'standard',
            policy_version: overrides.policyVersion || '2026-04-05.r10.tier-free.v1',
            policy_etag: overrides.policyEtag || 'W/"analysis-policy-9e7b4f"',
            effective_tier: overrides.tier || 'standard'
        },
        is_legacy: false,
        legacy_fallback_info: null,
        ...overrides.raw
    };
};

export {
    standardSubjectReady,
    subjectAiCompleted,
    subjectAiFailedRetryable,
    subjectWithUpgradeOffer,
    legacySubject,
    subjectReentryAwaitingTone,
    failedRollForwardPendingSubject,
    deliveryInitFailedRetryableSubject,
    manualRecoveryPendingSubject,
    cloneSubjectPayload,
    applyCapabilityUnlockToSubject,
    stringifyCapabilitySetKey,
    DEFAULT_GRANTED_CAPABILITY_CODES,
    DEFAULT_UNLOCKABLE_CAPABILITY_CREDITS,
    DEFAULT_UNAVAILABLE_CAPABILITY_REASONS
};
